import React, { useEffect } from 'react';
import ImageCarousel from '../components/ImageCarousel';
import { hostImages } from '../mock';
import SEO from '../components/SEO';
import PageActions from '../components/PageActions';

const HostPage = () => {
  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  const sections = [
    {
      id: 'food',
      title: 'Farm-Fresh Food',
      images: hostImages.food,
      description: 'It\'s how we live here. Fresh vegetables from the garden, eggs from our hens, and meals prepared with care. Good food is more than sustenance - it\'s connection, comfort, and story.'
    },
    {
      id: 'animals',
      title: 'Animals',
      images: hostImages.animals,
      description: 'Meet the farm\'s gentle residents: hens scratching in the yard, cows grazing the fields, and curious alpacas that always amuse. These animals aren\'t attractions - they\'re family, a reminder of rhythms older than our modern pace.'
    },
    {
      id: 'safety-support',
      title: 'Care Close at Hand',
      images: hostImages.safety,
      description: 'This is your place to settle. Your farmer-host is nearby but never intrusive - a quiet presence who cares for the land and is available if you need support or local knowledge but otherwise leaves you to enjoy your cabin in peace. Privacy comes first; assistance is simply available when you require.'
    },
    {
      id: 'sauna',
      title: 'Sauna',
      images: hostImages.sauna,
      description: 'After a day spent walking or simply being, the sauna offers deep restoration. Heat that releases tension, steam that clears the mind, followed by the shock of cool air. This Nordic tradition has been embraced here as another way to honour the body and deepen your connection to simplicity.'
    },
    {
      id: 'garden',
      title: 'Your Private Garden',
      images: hostImages.garden,
      description: 'Each country cabin has its own vegetable plot. Tend it if you wish, harvest fresh herbs for your meals, or simply enjoy watching things grow. There\'s something profound about getting your hands in soil, about eating what you\'ve nurtured. This is optional, but many find it becomes a highlight.'
    }
  ];

  return (
    <div className="bg-[#f5f1e8]">
      <SEO 
        title="Your Host - Farm-to-Table Experience in Dorset | Your Setl"
        description="Meet your farmer-host who shares authentic farm life. Fresh farm food, friendly animals, private garden, sauna, and genuine care. Experience a lifestyle that helps you truly unwind."
        keywords="farm stay Dorset, farm-to-table UK, farmer host, authentic farm experience, farm animals, rural lifestyle, farm fresh food, working farm stay, agricultural tourism UK"
        ogImage="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402346/hires-4788_gybyal.jpg"
        url="https://www.yoursetl.co.uk/your-host"
      />
      <PageActions />
      {/* Hero Section */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${hostImages.animals[0]})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 px-8 md:px-16 pb-16 md:pb-20 max-w-3xl">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#f5f1e8] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Your Host
          </h1>
          <div className="w-16 h-px bg-[#f5f1e8]/70 my-5" />
          <p
            className="text-base md:text-lg font-light text-[#f5f1e8] tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            More than accommodation. A way of life, shared with care and respect for nature's rhythms.
          </p>
        </div>
      </section>

      {/* Scrolling Sections */}
      {sections.map((section, index) => (
        <section
          key={section.id}
          id={section.id}
          className={`min-h-screen flex items-center py-20 px-6 ${
            index % 2 === 0 ? 'bg-[#f5f1e8]' : 'bg-[#e8e2d8]'
          }`}
        >
          <div className="max-w-7xl mx-auto w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div className={index % 2 === 0 ? 'order-1' : 'order-1 lg:order-2'}>
                <h2
                  className="text-4xl md:text-5xl font-light tracking-wide text-[#3a2f28] mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {section.title}
                </h2>
                <p
                  className="text-xl leading-relaxed text-[#3a2f28]/85"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {section.description}
                </p>
              </div>

              {/* Image Carousel */}
              <div className={index % 2 === 0 ? 'order-2' : 'order-2 lg:order-1'}>
                <div className="relative h-[400px] md:h-[550px] rounded-lg overflow-hidden shadow-2xl">
                  <ImageCarousel images={section.images} alt={section.title} />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Philosophy Section */}
      <section className="py-20 px-6 bg-[#3a2f28]">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl md:text-5xl font-light tracking-wide text-[#f5f1e8] mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            A Life That Helps You Unwind
          </h2>
          <p
            className="text-lg leading-relaxed text-[#f5f1e8]/90 mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Your host lives this philosophy daily. Not as a performance, but as a genuine way of being. Through their care, their farm, and their deep respect for nature's rhythms, they create space for you to rediscover your own.
          </p>
          <p
            className="text-base leading-relaxed text-[#f5f1e8]/80"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            This isn't about services rendered. It's about human connection, shared values, and the quiet understanding that sometimes the best hospitality is knowing when to simply let someone be.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HostPage;